import { BaseService, ServiceError, ValidationSchema } from './BaseService';
import { 
  twilioConfigRepository, 
  TwilioConfig 
} from '../repositories/TwilioConfigRepository';

/**
 * Input type for creating/updating Twilio config
 */
export interface UpsertTwilioConfigInput {
  account_sid?: string;
  auth_token_encrypted?: string;
  main_phone_number?: string;
  webhook_url?: string;
  recording_enabled?: boolean;
  transcription_enabled?: boolean;
  transcription_provider?: TwilioConfig['transcription_provider'];
}

/**
 * Service for managing Twilio integration configuration
 * Handles business logic and validation for Twilio settings
 * NOTE: Each company has exactly one Twilio config
 */
export class TwilioConfigService extends BaseService {
  /**
   * Validation schema for Twilio config
   */
  private twilioSchema: ValidationSchema = {
    account_sid: {
      required: false,
      type: 'string',
      pattern: /^AC[0-9a-f]{32}$/i,
      message: 'Invalid Twilio Account SID format'
    },
    main_phone_number: {
      required: false,
      type: 'phone',
      message: 'Invalid phone number format'
    },
    webhook_url: {
      required: false,
      type: 'string',
      pattern: /^https?:\/\/.+/,
      message: 'Webhook URL must be a valid HTTP(S) URL'
    }
  };

  /**
   * Get Twilio config for a company
   */
  async getConfig(companyId: string): Promise<TwilioConfig | null> {
    try {
      return await twilioConfigRepository.findByCompany(companyId);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Create or update Twilio config for a company
   */
  async upsertConfig(
    companyId: string,
    input: UpsertTwilioConfigInput
  ): Promise<TwilioConfig> {
    try {
      // Validate input
      if (Object.keys(input).length === 0) {
        throw ServiceError.validation([{
          field: 'input',
          message: 'At least one field must be provided'
        }]);
      }

      this.validate(input, this.twilioSchema);

      // Sanitize data
      const sanitized: Partial<TwilioConfig> = {};
      
      if (input.account_sid) {
        sanitized.account_sid = input.account_sid.trim();
      }
      if (input.auth_token_encrypted) {
        sanitized.auth_token_encrypted = input.auth_token_encrypted;
      }
      if (input.main_phone_number) {
        sanitized.main_phone_number = this.normalizePhone(input.main_phone_number) || undefined;
      }
      if (input.webhook_url) {
        sanitized.webhook_url = input.webhook_url.trim();
      }
      if (input.recording_enabled !== undefined) {
        sanitized.recording_enabled = input.recording_enabled;
      }
      if (input.transcription_enabled !== undefined) {
        sanitized.transcription_enabled = input.transcription_enabled;
      }
      if (input.transcription_provider) {
        sanitized.transcription_provider = input.transcription_provider;
      }

      // Upsert config
      const config = await twilioConfigRepository.upsert(companyId, sanitized);

      // Emit event
      const existing = await twilioConfigRepository.findByCompany(companyId);
      const event = existing ? 'twilio.config.updated' : 'twilio.config.created';
      await this.emit(event, { config });

      return config;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update Twilio auth token (encrypted)
   * This is a sensitive operation requiring proper encryption
   */
  async updateAuthToken(
    companyId: string,
    encryptedToken: string
  ): Promise<TwilioConfig> {
    try {
      if (!encryptedToken || encryptedToken.trim().length === 0) {
        throw ServiceError.validation([{
          field: 'auth_token',
          message: 'Auth token is required'
        }]);
      }

      const config = await twilioConfigRepository.updateAuthToken(
        companyId,
        encryptedToken
      );

      await this.emit('twilio.auth_token.updated', { companyId });

      return config;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Toggle call recording
   */
  async toggleRecording(companyId: string): Promise<TwilioConfig> {
    try {
      const config = await twilioConfigRepository.toggleRecording(companyId);
      await this.emit('twilio.recording.toggled', { config });
      return config;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Toggle transcription
   */
  async toggleTranscription(companyId: string): Promise<TwilioConfig> {
    try {
      const config = await twilioConfigRepository.toggleTranscription(companyId);
      await this.emit('twilio.transcription.toggled', { config });
      return config;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update transcription provider
   */
  async updateTranscriptionProvider(
    companyId: string,
    provider: TwilioConfig['transcription_provider']
  ): Promise<TwilioConfig> {
    try {
      const validProviders = ['deepgram', 'openai', 'google'];
      if (!validProviders.includes(provider)) {
        throw ServiceError.validation([{
          field: 'provider',
          message: 'Invalid transcription provider'
        }]);
      }

      const config = await twilioConfigRepository.updateTranscriptionProvider(
        companyId,
        provider
      );

      await this.emit('twilio.transcription_provider.updated', { config });

      return config;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update phone number
   */
  async updatePhoneNumber(
    companyId: string,
    phoneNumber: string
  ): Promise<TwilioConfig> {
    try {
      const normalized = this.normalizePhone(phoneNumber);
      if (!normalized) {
        throw ServiceError.validation([{
          field: 'phone_number',
          message: 'Invalid phone number format'
        }]);
      }

      const config = await twilioConfigRepository.updatePhoneNumber(
        companyId,
        normalized
      );

      await this.emit('twilio.phone_number.updated', { config });

      return config;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Test Twilio connection
   * Validates that the credentials work
   */
  async testConnection(companyId: string): Promise<{ success: boolean; message: string }> {
    try {
      const config = await this.getConfig(companyId);
      
      if (!config) {
        throw ServiceError.notFound('Twilio configuration', companyId);
      }

      if (!config.account_sid || !config.auth_token_encrypted) {
        throw ServiceError.validation([{
          field: 'credentials',
          message: 'Twilio credentials are incomplete'
        }]);
      }

      // Here you would actually test the connection to Twilio
      // For now, we'll just validate the format and return success
      // In production, you'd decrypt the token and make an API call to Twilio

      await this.emit('twilio.connection.tested', { companyId, success: true });

      return {
        success: true,
        message: 'Twilio connection successful'
      };
    } catch (error) {
      await this.emit('twilio.connection.tested', { companyId, success: false });
      this.handleError(error);
    }
  }
}

// Export singleton instance
export const twilioConfigService = new TwilioConfigService();
